import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ShieldCheck, FileText, ArrowLeft, AlertCircle } from 'lucide-react';

function PolitiqueConfidentialite({ childPolicy = false }) {
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { version } = useParams();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchPolicy = async (versionToFetch) => {
      try {
        setLoading(true);
        // La version de consentement de l'application est datée (AAAAMMJJ),
        // tandis que le document public historique porte le numéro juridique v3.
        const policyFileVersion = versionToFetch === 20260819 ? 3 : versionToFetch;
        const policyFileName = childPolicy
          ? 'politique-confidentialite-enfant_20260819.md'
          : `politique-confidentialite_${policyFileVersion}.md`;
        const policyUrl = `${process.env.PUBLIC_URL}/politiques/${policyFileName}`;
        const policyResponse = await fetch(policyUrl, { signal });

        if (!policyResponse.ok) {
          throw new Error(`Impossible de charger la politique de confidentialité version ${versionToFetch} (Erreur ${policyResponse.status}).`);
        }

        const text = await policyResponse.text();
        setMarkdown(text);
        setError('');
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error(`Erreur lors du chargement de la politique v${versionToFetch}:`, err);
          setError(err.message);
          setMarkdown('');
        }
      } finally {
        setLoading(false);
      }
    };

    const findAndFetchLatestPolicy = async () => {
      try {
        setLoading(true);
        const versionsUrl = `${process.env.PUBLIC_URL}/versions.json`;
        const versionsResponse = await fetch(versionsUrl, { signal });

        if (!versionsResponse.ok) {
          throw new Error(`Fichier d'index des versions introuvable (versions.json).`);
        }

        const versionsData = await versionsResponse.json();

        if (!versionsData || !Array.isArray(versionsData.versions) || versionsData.versions.length === 0) {
          throw new Error('Index des versions invalide ou vide.');
        }

        const latestVersion = Math.max(...versionsData.versions);
        await fetchPolicy(latestVersion);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error("Erreur lors de la recherche de la dernière politique :", err);
          setError(err.message);
          setMarkdown('');
        }
        setLoading(false);
      }
    };

    if (childPolicy) {
      fetchPolicy(20260819);
    } else if (version && !isNaN(parseInt(version, 10))) {
      fetchPolicy(parseInt(version, 10));
    } else {
      findAndFetchLatestPolicy();
    }

    return () => {
      controller.abort();
    };
  }, [version, childPolicy]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--mga-navy-deep)', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      <style>{`
        .markdown-content h1 {
          font-size: 2rem;
          color: var(--mga-gold);
          border-bottom: 1px solid rgba(242, 184, 7, 0.2);
          padding-bottom: 12px;
          margin-top: 24px;
          margin-bottom: 16px;
        }
        .markdown-content h2 {
          font-size: 1.5rem;
          color: var(--mga-white);
          margin-top: 28px;
          margin-bottom: 14px;
        }
        .markdown-content h3 {
          font-size: 1.2rem;
          color: var(--mga-gold-light);
          margin-top: 20px;
          margin-bottom: 10px;
        }
        .markdown-content p {
          color: var(--mga-slate-200);
          line-height: 1.8;
          font-size: 1.02rem;
          margin-bottom: 16px;
        }
        .markdown-content ul, .markdown-content ol {
          color: var(--mga-slate-200);
          line-height: 1.8;
          padding-left: 24px;
          margin-bottom: 20px;
        }
        .markdown-content li {
          margin-bottom: 8px;
        }
        .markdown-content strong {
          color: var(--mga-white);
        }
        .markdown-content a {
          color: var(--mga-gold);
          text-decoration: underline;
        }
        .markdown-content blockquote {
          border-left: 4px solid var(--mga-gold);
          padding: 12px 20px;
          background: rgba(242, 184, 7, 0.08);
          border-radius: 0 12px 12px 0;
          margin: 20px 0;
          color: var(--mga-gold-light);
        }
      `}</style>

      <main style={{ flex: 1, padding: '50px 20px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          {/* Top back button */}
          <div style={{ marginBottom: '25px' }}>
            <Link to="/" className="button-secondary" style={{ display: 'inline-flex', gap: '8px', padding: '10px 18px', fontSize: '0.9rem' }}>
              <ArrowLeft size={16} />
              Retour à l'accueil
            </Link>
          </div>

          {/* Header Banner */}
          <div className="text-center" style={{ marginBottom: '40px' }}>
            <span className="badge-gold">
              <ShieldCheck size={16} color="var(--mga-gold)" />
              Document Officiel RGPD
            </span>
            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)', color: 'var(--mga-white)', marginTop: '10px', marginBottom: '10px' }}>
              {childPolicy ? 'Politique MonAlerteEnfant' : 'Politique de Confidentialité'}
            </h1>
            <p style={{ color: 'var(--mga-slate-300)', fontSize: '1.05rem', maxWidth: '650px', margin: '0 auto' }}>
              {childPolicy
                ? 'Une information claire pour l’enfant et ses responsables légaux sur les données utilisées pour sa protection.'
                : 'Transparence totale sur la protection, la conservation et l’utilisation de vos données personnelles.'}
            </p>
            {!childPolicy && (
              <Link
                to="/politique-confidentialite-enfant"
                className="button-secondary"
                style={{ display: 'inline-flex', marginTop: '18px', padding: '10px 18px', fontSize: '0.9rem' }}
              >
                Politique dédiée à MonAlerteEnfant
              </Link>
            )}
          </div>

          {/* Main Content Glass Box */}
          <div className="glass-card" style={{
            padding: '45px 40px',
            background: 'rgba(11, 31, 58, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
            borderRadius: '24px'
          }}>
            {loading && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--mga-slate-300)' }}>
                <FileText size={40} color="var(--mga-gold)" style={{ marginBottom: '15px', animation: 'spin 2s linear infinite' }} />
                <p>Chargement du document de confidentialité...</p>
              </div>
            )}

            {error && (
              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                padding: '20px',
                color: '#f87171',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <AlertCircle size={24} />
                <div>
                  <strong>Erreur de chargement :</strong>
                  <p style={{ margin: '4px 0 0 0', color: '#fca5a5', fontSize: '0.95rem' }}>{error}</p>
                </div>
              </div>
            )}

            {!loading && !error && (
              <div className="markdown-content">
                <ReactMarkdown>{markdown}</ReactMarkdown>
              </div>
            )}
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}

export default PolitiqueConfidentialite;

export function PolitiqueConfidentialiteEnfant() {
  return <PolitiqueConfidentialite childPolicy />;
}
